import isEqual from "lodash-es/isEqual";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useEffect, useReducer, useState } from "react";
import { Grid } from "@mui/material";
// Components
import Button from "@/Layouts/components/Buttons/Button";
// Icons
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";
// Utils
import { replaceStatesWithWarnings } from "@/Settings/helpers/AdministrationTools/LocationDealerCodes/utils";
import { useMutation } from "@tanstack/react-query";
import { updateLocation } from "@/Common/api/location/mutations";
import { errorLogger } from "@/Common/helpers/logger";
import { EActionType, TLocationDealerCodeState, IOemMetaData, TErrors } from "@/Settings/types/administrationTools/locationDealerCodes";
import { formReducer, initialOemState } from "@/Settings/helpers/AdministrationTools/LocationDealerCodes/reducer";
import { arrayOfDealerCodesSchema } from "@/Settings/helpers/AdministrationTools/LocationDealerCodes/validation";
import OemLayout from "./OemLayout";

type LocationDealerCodesProps = { oemMetaData: IOemMetaData[]; locationId: string };

const LocationDealerCodes = ({ oemMetaData, locationId }: LocationDealerCodesProps) => {
	const [errors, setErrors] = useState<TErrors>(null);
	const [warningPaths, setWarningPaths] = useState<number[][]>([]);
	const location = useSelector((locationState: RootState) => locationState.location.location);
	const oemState = (location?.oems as TLocationDealerCodeState) ?? initialOemState;
	const [state, dispatch] = useReducer(formReducer, oemState);

	const { mutate, isPending } = useMutation({
		mutationKey: ["updateLocationDealerCodes", locationId],
		mutationFn: (input: { oems: TLocationDealerCodeState }) => updateLocation(locationId, input, "STORE_OEMS"),
		onError: (error) => {
			errorLogger(error);
		},
	});

	const handleSetWarningPaths = (path: number[], action: string) => {
		if (action === "add") {
			setWarningPaths((prev) => [...prev, path]);
		} else if (action === "remove" && path.length === 1) {
			setWarningPaths((prev) => prev.filter((item) => item[0] !== path[0]));
		} else {
			setWarningPaths((prev) => prev.filter((item) => !isEqual(item, path)));
		}
	};

	const handleAddOem = () => {
		dispatch({ type: EActionType.ADD_OEM, payload: {} });
	};

	useEffect(() => {
		const finalState = replaceStatesWithWarnings(state, oemState, warningPaths);
		dispatch({ type: EActionType.UPDATE_STATE, payload: {}, newState: finalState });
	}, [oemState]);

	useEffect(() => {
		const validation = arrayOfDealerCodesSchema.validate(state, {
			allowUnknown: true,
			presence: "optional",
			abortEarly: false,
		});
		setErrors(validation?.error ?? null);

		const finalState = replaceStatesWithWarnings(oemState, state, warningPaths);
		if (!warningPaths?.length && !validation.error && !isEqual(finalState, oemState)) {
			mutate({ oems: finalState });
		}
		return () => {
			setErrors(null);
		};
	}, [state]);

	const disabledStores = state.map((oem) => oem.dba);

	return (
		<Grid container item>
			{state.map((oem, oemIndex) => {
				const keyIndex = `${oem.oemReference}-${oemIndex}`;
				return (
					<Grid key={keyIndex} item xs={12}>
						<OemLayout
							oems={state}
							oem={oem}
							storesDetails={location?.storesDetails || []}
							disabledStores={disabledStores}
							oemMetaData={oemMetaData}
							dispatch={dispatch}
							oemIndex={oemIndex}
							errors={errors}
							warningPaths={warningPaths}
							onSetWarningPaths={handleSetWarningPaths}
							isPending={isPending}
						/>
					</Grid>
				);
			})}
			<Grid item xs={10} mt={1} mb={4} justifyContent="center" alignItems="flex-start">
				<Button
					variant="contained"
					text="Add OEM"
					classes="btn-regular-add btn-small-height btn-regular-text"
					minWidth={"130px"}
					endIcon={<AddCircleRounded />}
					onClick={handleAddOem}
					disabled={isPending}
				/>
			</Grid>
		</Grid>
	);
};

export default LocationDealerCodes;
